import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import {
  MARKETING_ICON,
  MARKETING_ICON_FILLED,
} from "../../../../redo-suite/marketing-cloud";

export type EmailSmsSubRoute =
  | "welcome"
  | "ai-personas"
  | "audience"
  | "deliverability"
  | "campaigns"
  | "segments"
  | "templates/email"
  | "templates/sms"
  | "signups"
  | "popups"
  | "spotlights";

export const EmailSMSItems = memo(function MarketingItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const settings = team.settings;

  const emailSmsRoot = routes.emailSmsHref;

  const marketingEnabled = settings.marketing?.enabled;

  const showCampaigns =
    marketingEnabled && settings?.marketing?.campaigns?.enabled;

  const showAiPersonas = marketingEnabled && settings?.marketing?.ai?.enabled;

  // TODO: enable when feature is ready
  const showDeliverabilityReport = false;

  const anyChildSelected = useMatch(`${emailSmsRoot}/*`);

  const primaryHref = marketingEnabled
    ? emailSmsRoot
    : routes.getEmailSmsSubRoute("welcome");

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: primaryHref }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={MARKETING_ICON_FILLED}
      friendlyName="Email & SMS"
      id="email-sms"
      UnfilledIcon={MARKETING_ICON}
    >
      <EmailSmsAiPersonasItem hidden={!showAiPersonas} />
      <EmailSmsAudienceItem />
      <EmailSmsCampaignsItem hidden={!showCampaigns} />
      <EmailSmsDeliverabilityItem hidden={!showDeliverabilityReport} />
      <EmailSmsEmailLibraryItem />
      <EmailSmsSegmentsItem hidden={!showCampaigns} />
      <EmailSmsSignUpsItem hidden={!marketingEnabled} />
      <EmailSmsSmsTemplatesItem />
      <EmailSmsPopupsItem hidden={!marketingEnabled} />
      <EmailSmsWelcomeItem hidden={marketingEnabled} />
    </RedoSecondaryNavItem>
  );
});

const EmailSmsAiPersonasItem = memo(function EmailSmsAiPersonasItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("ai-personas") }}
      friendlyName="AI personas"
      hidden={hidden}
      id="ai-personas"
    />
  );
});

const EmailSmsAudienceItem = memo(function EmailSmsAudienceItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("audience") }}
      friendlyName="Audience"
      id="audience"
    />
  );
});

const EmailSmsCampaignsItem = memo(function EmailSmsCampaignsItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("campaigns") }}
      friendlyName="Campaigns"
      hidden={hidden}
      id="campaigns"
    />
  );
});

const EmailSmsDeliverabilityItem = memo(function EmailSmsDeliverabilityItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getEmailSmsSubRoute("deliverability"),
      }}
      friendlyName="Deliverability"
      hidden={hidden}
      id="deliverability"
    />
  );
});

const EmailSmsEmailLibraryItem = memo(function EmailSmsEmailLibraryItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getEmailSmsSubRoute("templates/email"),
      }}
      friendlyName="Email library"
      id="email-templates"
    />
  );
});

const EmailSmsSegmentsItem = memo(function EmailSmsSegmentsItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("segments") }}
      friendlyName="Segments"
      hidden={hidden}
      id="segments"
    />
  );
});

const EmailSmsSignUpsItem = memo(function EmailSmsSignUpsItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("signups") }}
      friendlyName="Sign-ups"
      hidden={hidden}
      id="signups"
    />
  );
});

const EmailSmsSmsTemplatesItem = memo(function EmailSmsSmsTemplatesItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getEmailSmsSubRoute("templates/sms"),
      }}
      friendlyName="SMS templates"
      id="sms-templates"
    />
  );
});

const EmailSmsPopupsItem = memo(function EmailSmsPopupsItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("popups") }}
      friendlyName="Popups"
      hidden={hidden}
      id="popups"
    />
  );
});

const EmailSmsWelcomeItem = memo(function EmailSmsWelcomeItem({
  hidden,
}: {
  hidden?: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getEmailSmsSubRoute("welcome") }}
      friendlyName="Welcome"
      hidden={hidden}
      id="welcome"
    />
  );
});
